import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Sparkles, Trash2, Heart, MessageCircle, Eye, Pin, PinOff, Send, Link as LinkIcon,
  ChevronRight, Flame, Rocket, PartyPopper, Hand, Zap as ZapIcon, Tag, Search, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AttachmentPreview from "../../components/AttachmentPreview";
import MultiUploader from "../../components/MultiUploader";
import Loading from "../../components/Loading";

const REACTION_SET = [
  { emoji: "❤️", Icon: Heart },
  { emoji: "🔥", Icon: Flame },
  { emoji: "🚀", Icon: Rocket },
  { emoji: "👏", Icon: PartyPopper },
  { emoji: "🙌", Icon: Hand },
  { emoji: "🤯", Icon: ZapIcon },
];

function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return ""; } }

/* -------------------------------------------- Composer */
function CreationComposer({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [link, setLink] = useState("");
  const [attIds, setAttIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        tags: tags.split(",").map(s => s.trim()).filter(Boolean),
        link: link.trim() || null,
        attachment_ids: attIds,
      };
      const r = await api.post("/community-creations", payload);
      onCreated(r.data);
      setTitle(""); setBody(""); setTags(""); setLink(""); setAttIds([]);
      setOpen(false);
      toast.success("CREATION TRANSMITTED");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SUBMIT FAILED");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="sat-btn sat-btn-solid" data-testid="creation-new-btn">
        <Plus size={12} /> POST CREATION
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="sat-brackets sat-surface border border-[color:var(--sat-cyan)] p-4 space-y-3" data-testid="creation-composer">
      <div className="sat-label sat-cyan flex items-center gap-2"><Sparkles size={12} /> NEW CREATION</div>
      <input
        data-testid="creation-title" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="TITLE" maxLength={140} required minLength={3}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm"
      />
      <textarea
        data-testid="creation-body" value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="Description — what did you build, film, draw, or code?"
        rows={4} maxLength={6000}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-center gap-2 border border-[color:var(--sat-border)] focus-within:border-[color:var(--sat-cyan)] px-3 py-2">
          <Tag size={13} className="sat-cyan" />
          <input
            data-testid="creation-tags" value={tags} onChange={(e) => setTags(e.target.value)}
            placeholder="tags, comma, separated (max 6)"
            className="flex-1 bg-transparent outline-none font-mono text-sm"
          />
        </div>
        <div className="flex items-center gap-2 border border-[color:var(--sat-border)] focus-within:border-[color:var(--sat-cyan)] px-3 py-2">
          <LinkIcon size={13} className="sat-cyan" />
          <input
            data-testid="creation-link" value={link} onChange={(e) => setLink(e.target.value)}
            placeholder="optional game / video / project link"
            className="flex-1 bg-transparent outline-none font-mono text-sm"
          />
        </div>
      </div>
      <MultiUploader ids={attIds} onChange={setAttIds} max={8} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="sat-btn !py-2 !px-3">CANCEL</button>
        <button type="submit" disabled={busy || !title.trim()} data-testid="creation-submit"
          className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
          {busy ? "TRANSMITTING…" : "POST"}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------- Comment strip */
function CommentSection({ creation, currentUser, isAdmin, onCommentChange }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const isOP = currentUser && currentUser.id === creation.author_id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/community-creations/${creation.id}/comments`);
        if (!cancelled) setComments(r.data.results || []);
      } catch { /* silent */ } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [creation.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/community-creations/${creation.id}/comments`, { body });
      setComments(cur => [...cur, r.data]);
      onCommentChange(1);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "COMMENT FAILED");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/community-creations/${creation.id}/comments/${c.id}`);
      setComments(cur => cur.filter(x => x.id !== c.id));
      onCommentChange(-1);
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };

  const opReact = async (c, emoji) => {
    try {
      const r = await api.post(`/community-creations/${creation.id}/comments/${c.id}/react`, { emoji });
      setComments(cur => cur.map(x => x.id === c.id ? { ...x, op_reactions: r.data.op_reactions } : x));
    } catch (err) { toast.error(err?.response?.data?.detail || "REACT FAILED"); }
  };

  return (
    <div className="mt-4 space-y-3 border-t border-[color:var(--sat-border)] pt-3">
      <div className="sat-label sat-cyan flex items-center gap-2">
        <MessageCircle size={11} /> COMMENTS · {comments.length}
      </div>
      {loading && <div className="sat-label text-[color:var(--sat-text-dim)]">LOADING…</div>}
      {!loading && comments.length === 0 && (
        <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid={`creation-comments-empty-${creation.id}`}>
          NO COMMENTS YET.
        </div>
      )}
      <ul className="space-y-2" data-testid={`creation-comments-${creation.id}`}>
        {comments.map(c => {
          const canDelete = currentUser && (c.author_id === currentUser.id || isOP || isAdmin);
          return (
            <li key={c.id} className="border-l-2 border-[color:var(--sat-border)] pl-3 py-1.5" data-testid={`creation-comment-${c.id}`}>
              <div className="sat-label sat-cyan flex items-center gap-2 flex-wrap">
                @{c.author_callsign} · {fmtTime(c.created_at)}
                {canDelete && (
                  <button onClick={() => del(c)} className="hover:text-red-400"
                    data-testid={`creation-comment-delete-${c.id}`} title="Delete">
                    <Trash2 size={10} />
                  </button>
                )}
              </div>
              <div className="text-sm whitespace-pre-wrap mt-1">{c.body}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {Object.entries(c.op_reactions || {}).map(([em, n]) => (
                  <span key={em} className="border border-[color:var(--sat-cyan)]/50 sat-cyan text-xs px-1.5 py-0.5 font-mono"
                    data-testid={`creation-op-reaction-badge-${c.id}-${em}`}>
                    {em} {n}
                  </span>
                ))}
                {isOP && (
                  <div className="flex items-center gap-1 ml-auto">
                    <span className="sat-label text-[9px] text-[color:var(--sat-text-dim)]">OP REACT</span>
                    {REACTION_SET.map(({ emoji }) => (
                      <button
                        key={emoji}
                        onClick={() => opReact(c, emoji)}
                        data-testid={`creation-op-react-${c.id}-${emoji}`}
                        className="text-xs hover:scale-125 transition-transform"
                        title={`React ${emoji}`}
                      >{emoji}</button>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {currentUser ? (
        <form onSubmit={submit} className="flex items-start gap-2">
          <textarea
            data-testid={`creation-comment-input-${creation.id}`}
            value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Drop a comment…" rows={2} maxLength={2000}
            className="flex-1 bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none"
          />
          <button type="submit" disabled={busy || !body.trim()} data-testid={`creation-comment-submit-${creation.id}`}
            className="sat-btn sat-btn-solid !py-2 !px-3 disabled:opacity-40">
            <Send size={11} />
          </button>
        </form>
      ) : (
        <div className="sat-label text-[color:var(--sat-text-dim)]">LOG IN TO COMMENT.</div>
      )}
    </div>
  );
}

/* -------------------------------------------- Card */
function CreationCard({ c, currentUser, isAdmin, onDelete, onPinToggle, onCommentDelta, onTagClick }) {
  const [open, setOpen] = useState(false);
  const [creation, setCreation] = useState(c);
  useEffect(() => { setCreation(c); }, [c]);
  const isOP = currentUser && currentUser.id === creation.author_id;
  const canDelete = currentUser && (isOP || isAdmin);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && currentUser && !isOP) {
      // fetch fresh detail to count a view
      try {
        const r = await api.get(`/community-creations/${creation.id}`);
        setCreation(r.data);
      } catch { /* silent */ }
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this creation?")) return;
    try {
      await api.delete(`/community-creations/${creation.id}`);
      onDelete(creation.id);
      toast.success("CREATION DELETED");
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };

  const togglePin = async () => {
    try {
      const r = await api.post(`/community-creations/${creation.id}/pin`);
      setCreation(cr => ({ ...cr, pinned_at: r.data.pinned ? r.data.pinned_at : null }));
      onPinToggle(creation.id, r.data.pinned);
      toast.success(r.data.pinned ? "PINNED" : "UNPINNED");
    } catch (err) { toast.error(err?.response?.data?.detail || "PIN FAILED"); }
  };

  const reactionTotal = Object.values(creation.reactions || {}).reduce((a, b) => a + b, 0);

  return (
    <article className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4"
      data-testid={`creation-card-${creation.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={toggle} className="flex-1 text-left" data-testid={`creation-toggle-${creation.id}`}>
          <div className="sat-label sat-cyan flex items-center gap-2 flex-wrap">
            <Sparkles size={11} /> @{creation.author_callsign}
            {creation.pinned_at && (
              <span className="border border-[color:var(--sat-cyan)] text-[color:var(--sat-cyan)] px-1.5 py-0.5 inline-flex items-center gap-1 text-[9px]">
                <Pin size={8} /> PINNED
              </span>
            )}
          </div>
          <div className="font-display font-bold text-lg mt-1 flex items-center gap-2">
            <span className="flex-1">{creation.title}</span>
            <ChevronRight size={16} className={`shrink-0 transition-transform ${open ? "rotate-90 sat-cyan" : ""}`} />
          </div>
          <div className="sat-label mt-1 text-[color:var(--sat-text-dim)] flex items-center gap-3 flex-wrap">
            <span>{fmtTime(creation.created_at)}</span>
            <span className="flex items-center gap-1"><Eye size={10} /> {creation.view_count}</span>
            <span className="flex items-center gap-1"><MessageCircle size={10} /> {creation.comment_count}</span>
            <span className="flex items-center gap-1"><Heart size={10} /> {reactionTotal}</span>
          </div>
        </button>
        <div className="flex gap-1 shrink-0">
          {isOP && (
            <button onClick={togglePin} className="sat-btn !py-1 !px-2"
              data-testid={`creation-pin-${creation.id}`} title={creation.pinned_at ? "Unpin" : "Pin"}>
              {creation.pinned_at ? <PinOff size={11} /> : <Pin size={11} />}
            </button>
          )}
          {canDelete && (
            <button onClick={del} className="sat-btn sat-btn-danger !py-1 !px-2"
              data-testid={`creation-delete-${creation.id}`} title="Delete">
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>

      {creation.tags && creation.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {creation.tags.map(t => (
            <button key={t} onClick={() => onTagClick && onTagClick(t)}
              data-testid={`creation-tag-${creation.id}-${t}`}
              className="border border-[color:var(--sat-border)] hover:border-[color:var(--sat-cyan)] text-[color:var(--sat-text-dim)] hover:sat-cyan font-mono text-[10px] px-1.5 py-0.5">
              #{t}
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-3">
          {creation.body && (
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{creation.body}</div>
          )}
          {creation.link && (
            <a href={creation.link} target="_blank" rel="noopener noreferrer"
              className="sat-cyan hover:underline inline-flex items-center gap-1 font-mono text-sm break-all"
              data-testid={`creation-link-${creation.id}`}>
              <LinkIcon size={11} /> {creation.link}
            </a>
          )}
          {creation.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {creation.attachments.map(a => <AttachmentPreview key={a.id} attachment={a} compact />)}
            </div>
          )}
          {Object.keys(creation.reactions || {}).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(creation.reactions).map(([em, n]) => (
                <span key={em} className="border border-[color:var(--sat-border-bright)] font-mono text-xs px-1.5 py-0.5">
                  {em} {n}
                </span>
              ))}
            </div>
          )}
          <CommentSection
            creation={creation}
            currentUser={currentUser}
            isAdmin={isAdmin}
            onCommentChange={(delta) => {
              setCreation(cr => ({ ...cr, comment_count: (cr.comment_count || 0) + delta }));
              onCommentDelta(creation.id, delta);
            }}
          />
        </div>
      )}
    </article>
  );
}

/* -------------------------------------------- Tab */
export default function CreationsTab() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [top, setTop] = useState([]);
  const [tail, setTail] = useState([]);
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("top");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("sort", sort);
      if (tag) params.set("tag", tag);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/community-creations?${params.toString()}`);
      setTop(r.data.top || []);
      setTail(r.data.tail || []);
      setTags(r.data.tags || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "LOAD FAILED");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, tag]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const addToFront = (c) => setTop(cur => [c, ...cur]);
  const removeAny = (id) => { setTop(cur => cur.filter(x => x.id !== id)); setTail(cur => cur.filter(x => x.id !== id)); };
  const bumpComment = (id, delta) => {
    const bump = (arr) => arr.map(x => x.id === id ? { ...x, comment_count: (x.comment_count || 0) + delta } : x);
    setTop(bump); setTail(bump);
  };
  const setPin = (id, pinned) => {
    const upd = (arr) => arr.map(x => x.id === id ? { ...x, pinned_at: pinned ? new Date().toISOString() : null } : x);
    setTop(upd); setTail(upd);
  };

  return (
    <div data-tab-key="creations" data-tab-content="1" className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md border border-[color:var(--sat-border)] focus-within:border-[color:var(--sat-cyan)] px-3 py-2">
          <Search size={13} className="sat-cyan" />
          <input
            data-testid="creation-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH TITLES / DESCRIPTIONS / TAGS"
            className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-[color:var(--sat-text-dim)]"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="creation-search-clear" className="sat-cyan"><XIcon size={12} /></button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSort("top")} data-testid="creation-sort-top"
            className={`sat-btn !py-2 !px-3 ${sort === "top" ? "sat-btn-solid" : ""}`}>TOP</button>
          <button onClick={() => setSort("new")} data-testid="creation-sort-new"
            className={`sat-btn !py-2 !px-3 ${sort === "new" ? "sat-btn-solid" : ""}`}>NEW</button>
        </div>
        {user && <CreationComposer onCreated={addToFront} />}
      </div>

      {/* Tag chips */}
      {(tags.length > 0 || tag) && (
        <div className="flex flex-wrap gap-1.5" data-testid="creation-tag-chips">
          {tag && (
            <button onClick={() => setTag("")} data-testid="creation-tag-clear"
              className="border border-[color:var(--sat-cyan)] sat-cyan font-mono text-[10px] px-2 py-0.5 flex items-center gap-1">
              CLEAR #{tag} <XIcon size={10} />
            </button>
          )}
          {tags.filter(([t]) => t !== tag).map(([t, n]) => (
            <button key={t} onClick={() => setTag(t)}
              data-testid={`creation-tag-chip-${t}`}
              className="border border-[color:var(--sat-border)] hover:border-[color:var(--sat-cyan)] text-[color:var(--sat-text-dim)] hover:sat-cyan font-mono text-[10px] px-2 py-0.5">
              #{t} · {n}
            </button>
          ))}
        </div>
      )}

      {loading && <Loading message="LOADING CREATIONS" />}

      {!loading && top.length === 0 && tail.length === 0 && (
        <div className="sat-label text-center py-10" data-testid="creation-empty">
          {q || tag
            ? "NO MATCHES — TRY A DIFFERENT KEYWORD OR TAG."
            : (user ? "NO CREATIONS YET — POST THE FIRST ONE." : "NO CREATIONS YET — LOG IN TO POST.")}
        </div>
      )}

      {sort === "top" && top.length > 0 && (
        <section className="space-y-3" data-testid="creation-section-top">
          <div className="sat-label sat-cyan flex items-center gap-2">
            <Flame size={11} /> TOP TRANSMISSIONS
          </div>
          {top.map(c => (
            <CreationCard
              key={c.id} c={c} currentUser={user} isAdmin={isAdmin}
              onDelete={removeAny} onPinToggle={setPin}
              onCommentDelta={bumpComment} onTagClick={setTag}
            />
          ))}
        </section>
      )}

      {tail.length > 0 && (
        <section className="space-y-3" data-testid="creation-section-tail">
          <div className="sat-label sat-cyan flex items-center gap-2">
            <Sparkles size={11} /> {sort === "top" ? "EVERY OTHER CREATION" : "ALL CREATIONS"}
          </div>
          {tail.map(c => (
            <CreationCard
              key={c.id} c={c} currentUser={user} isAdmin={isAdmin}
              onDelete={removeAny} onPinToggle={setPin}
              onCommentDelta={bumpComment} onTagClick={setTag}
            />
          ))}
        </section>
      )}
    </div>
  );
}
